import * as React from "react";
import { useNavigate } from "react-router-dom";
import { pick } from "lodash";
import { useAlert } from "../contexts/AlertProvider";
import { API_BASE_URL } from "../config";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
};

function useRegister() {
  const [showPassword, setShowPassword] = React.useState(false);
  const [formData, setFormData] = React.useState(initialFormData);
  const [loading, setLoading] = React.useState(false);
  const { alert } = useAlert();
  const navigate = useNavigate();

  const handleFormChange = (label, value) => {
    setFormData((prev) => ({
      ...prev,
      [label]: value,
    }));
  };

  const toggleShowPassword = () => {
    setShowPassword((show) => (show ? false : true));
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleReturn = () => {
    const path = "/";
    navigate(path);
  };

  const url = `${API_BASE_URL}/${"agents/register/v1"}`;

  const registerData = async () => {
    setLoading(true);

    try {
      if (formData.confirmPassword !== formData.password) {
        alert({
          title: "Account Creation Error",
          detail: `Passwords do not match`,
          severity: "error",
          duration: 5000,
        });
        return;
      }

      const payload = pick(formData, [
        "firstName",
        "lastName",
        "email",
        "password",
      ]);

      const options = {
        method: "POST", // *GET, POST, PUT, DELETE, etc.
        mode: "cors", // no-cors, *cors, same-origin
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      };

      const response = await fetch(url, options);
      const result = await response.json();

      if (result.status === "WARNING") {
        alert({
          title: "Account Creation Warning",
          detail: result.failureReason,
          severity: "warning", // or 'success', 'warning', 'error'
          duration: 8000, // duration in milliseconds
        });
        return result.data;
      }

      if (result.status !== "SUCCESS") {
        alert({
          title: "Account Creation Error",
          detail: result.failureReason || "Unable to create an account at the moment",
          severity: "error",
          duration: 5000,
        });
        return;
      }

      alert({
        title: "New Account Created",
        detail: `A new account has been created! Check ${formData["email"]} for more details`,
        severity: "success",
        duration: 5000,
      });

      return result.data;
    } catch (error) {
      alert({
        title: "Account Creation Error",
        detail: "Unable to create an account at the moment",
        severity: "error", // or 'success', 'warning', 'error'
        duration: 5000, // duration in milliseconds
      });
    } finally {
      setLoading(false);
    }
  };

  const handlers = React.useMemo(
    () => ({
      toggle: toggleShowPassword,
      mouseDown: handleMouseDownPassword,
      return: handleReturn,
      handleFormChange: handleFormChange,
    }),
    []
  );

  const registerProps = {
    showPassword,
    handlers,
    registerData,
    formData,
    loading
  };

  return registerProps;
}

export default useRegister;
